import {
  collection,
  CollectionReference,
  doc,
  FirestoreError,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Query,
  QueryConstraint,
  QueryDocumentSnapshot,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "~/integrations/firebase/firestore";
import { logError } from "~/shared/services/error-reporting";
import type { IProduct } from "reedy-data";
import { FirestoreProductSubject } from "reedy-data/api";
import { nonNullable } from "~/utils/types";
import {
  FirestoreProductActionsKeywords,
  FirestoreProductActionsDescriptions,
  FirestoreProductActionsThema,
  FirestoreProductActionsSubtitle,
} from "reedy-data/models";

export { getDefaultDescription, getDescriptionActions } from "reedy-data";
export type { IProduct } from "reedy-data";

export const getActionsByType = (
  product?: IProduct,
): {
  keywords?: FirestoreProductActionsKeywords["actions"];
  descriptions?: FirestoreProductActionsDescriptions["actions"];
  thema?: FirestoreProductActionsThema["actions"];
  subtitle?: FirestoreProductActionsSubtitle["actions"];
} => ({
  keywords: product?.actions?.keywords?.actions,
  descriptions: product?.actions?.descriptions?.actions,
  thema: product?.actions?.thema?.actions,
  subtitle: product?.actions?.subtitle?.actions,
});

const productConverter = {
  toFirestore(product: IProduct) {
    return product;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot<IProduct>): IProduct {
    const data = snapshot.data();

    const { identifier } = data;

    return {
      ...data,
      id: snapshot.id,
      isbn: identifier,
    };
  },
};

export const getProductsCollection = () =>
  collection(db, "products").withConverter(productConverter);

export enum SubjectSchemeIdentifier {
  ThemaSubjectCategory = "themaSubjectCategory",
  ThemaPlaceQualifier = "themaPlaceQualifier",
  ThemaLanguageQualifier = "themaLanguageQualifier",
  ThemaTimePeriodQualifier = "themaTimePeriodQualifier",
  ThemaStyleQualifier = "themaStyleQualifier",
  ThemaEducationalPurposeQualifier = "themaEducationalPurposeQualifier",
  ThemaInterestAgeSpecialInterestQualifier = "themaInterestAgeSpecialInterestQualifier",
  Keyword = "keyword",
}

export const qualifierTypes = [
  SubjectSchemeIdentifier.ThemaPlaceQualifier,
  SubjectSchemeIdentifier.ThemaTimePeriodQualifier,
  SubjectSchemeIdentifier.ThemaLanguageQualifier,
  SubjectSchemeIdentifier.ThemaStyleQualifier,
  SubjectSchemeIdentifier.ThemaEducationalPurposeQualifier,
  SubjectSchemeIdentifier.ThemaInterestAgeSpecialInterestQualifier,
];
export const subjectsType = SubjectSchemeIdentifier.ThemaSubjectCategory;

export const getSubjectSchemeIdentifier = (
  code: string,
): SubjectSchemeIdentifier => {
  const firstChar = code.charAt(0);

  if (firstChar === "1") {
    return SubjectSchemeIdentifier.ThemaPlaceQualifier;
  }
  if (firstChar === "2") {
    return SubjectSchemeIdentifier.ThemaLanguageQualifier;
  }
  if (firstChar === "3") {
    return SubjectSchemeIdentifier.ThemaTimePeriodQualifier;
  }
  if (firstChar === "4") {
    return SubjectSchemeIdentifier.ThemaEducationalPurposeQualifier;
  }
  if (firstChar === "5") {
    return SubjectSchemeIdentifier.ThemaInterestAgeSpecialInterestQualifier;
  }
  if (firstChar === "6") {
    return SubjectSchemeIdentifier.ThemaStyleQualifier;
  }
  return SubjectSchemeIdentifier.ThemaSubjectCategory;
};

export type ProductSubjectType = "generated" | "database" | "final";

export type KeywordSubjectType = ProductSubjectType | "finalWords";

export const keywordsToText = (keywords: FirestoreProductSubject[]) =>
  keywords.map((keyword) => keyword.subjectHeadingText).filter(nonNullable);

export const countKeywordCharacters = (
  subjects: FirestoreProductSubject[],
): number =>
  subjects.reduce((totalChars, subject) => {
    if (subject.subjectSchemeIdentifier !== SubjectSchemeIdentifier.Keyword) {
      return totalChars;
    }

    const keyword = subject.subjectHeadingText?.trim();
    if (!keyword) {
      return totalChars;
    }

    return totalChars + keyword.length;
  }, 0);

const BATCH_SIZE = 30;

const createBatches = (ids: string[]): string[][] =>
  ids.reduce(
    (acc, _, i) =>
      i % BATCH_SIZE === 0
        ? // biome-ignore lint/complexity/noCommaOperator: pushes the batch, then returns the accumulator
          (acc.push(ids.slice(i, i + BATCH_SIZE)), acc)
        : acc,
    [] as string[][],
  );

const queryWithBatchedIds = (
  ids: string[],
  products: CollectionReference<IProduct, IProduct>,
  ...queryConstraints: QueryConstraint[]
): Query[] => {
  const batches = createBatches(ids);
  const queries: Query<IProduct>[] = [];

  batches.forEach((batch) => {
    const q = query(
      products,
      ...[...queryConstraints, where("id", "in", batch)],
    );
    queries.push(q);
  });

  return queries;
};

const queryWithBatchedIsbns = (
  isbns: string[],
  products: CollectionReference<IProduct, IProduct>,
  ...queryConstraints: QueryConstraint[]
): Query[] => {
  const batches = createBatches(isbns);
  const queries: Query<IProduct>[] = [];

  batches.forEach((batch) => {
    const q = query(
      products,
      ...[...queryConstraints, where("identifier", "in", batch)],
    );
    queries.push(q);
  });

  return queries;
};

export const watchProducts = (
  ids: string[],
  organisationId: string,
  onNext: (products: IProduct[]) => void,
  onError?: (error: FirestoreError) => void,
) => {
  const allProducts: IProduct[] = [];

  const queries = queryWithBatchedIds(
    ids,
    getProductsCollection(),
    where("organisation.id", "==", organisationId),
  );

  const unsubscribeFunctions = queries.map((q) =>
    onSnapshot(
      q,
      (querySnapshot) => {
        const newProducts: IProduct[] =
          querySnapshot.docs?.map(
            (docum: QueryDocumentSnapshot) => docum.data() as IProduct,
          ) || [];

        newProducts.forEach((product) => {
          const productIndex = allProducts.findIndex(
            (p) => p.id === product.id,
          );
          if (productIndex !== -1) {
            allProducts[productIndex] = product;
          } else {
            allProducts.push(product);
          }
        });

        onNext([...allProducts]);
      },
      onError,
    ),
  );

  return () => {
    unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
  };
};

export const getProductsByIsbns = async (
  organisationId: string,
  isbns: string[],
): Promise<IProduct[]> => {
  const allProducts: IProduct[] = [];

  try {
    await Promise.all(
      queryWithBatchedIsbns(
        isbns,
        getProductsCollection(),
        where("organisation.id", "==", organisationId),
      ).map(async (q) => {
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((docum) => {
          allProducts.push(docum.data() as IProduct);
        });
      }),
    );
  } catch (error) {
    logError("Error fetching products by isbns:", { originalException: error });
    throw error; // Re-throw the error after logging it
  }

  return allProducts;
};

export const getProductsByIds = async (
  organisationId: string,
  ids: string[],
): Promise<IProduct[]> => {
  const allProducts: IProduct[] = [];

  try {
    await Promise.all(
      queryWithBatchedIds(
        ids,
        getProductsCollection(),
        where("organisation.id", "==", organisationId),
      ).map(async (q) => {
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((docum) => {
          allProducts.push(docum.data() as IProduct);
        });
      }),
    );
  } catch (error) {
    logError("Error fetching products by ids:", { originalException: error });
    throw error; // Re-throw the error after logging it
  }

  return allProducts;
};

// Requires the products-org-work-id composite index.
export const getProductsByWorkId = (organisationId: string, workId: string) =>
  query(
    getProductsCollection(),
    where("organisation.id", "==", organisationId),
    where("workId", "==", workId),
    orderBy("identifier"),
  );

export type UpdateFields = Record<string, any>;

export const updateProductInTransaction = async (
  productId: string,
  productFields: (product?: IProduct) => UpdateFields,
) => {
  const productRef = doc(getProductsCollection(), productId);

  await runTransaction(db, async (transaction) => {
    const product = await transaction.get(productRef);

    const updates = productFields(product.data());

    transaction.update(productRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  });
};

export const updateProduct = async (
  productId: string,
  productFields: UpdateFields,
) => {
  const productRef = doc(getProductsCollection(), productId);
  await updateDoc(productRef, {
    ...productFields,
    updatedAt: serverTimestamp(),
  });
};

export const getKeywordsByType = (
  product?: IProduct,
): Record<KeywordSubjectType, FirestoreProductSubject[]> => ({
  database: (product?.subjects?.database ?? []).filter(
    (subject) =>
      subject.subjectSchemeIdentifier === SubjectSchemeIdentifier.Keyword,
  ),
  generated: (product?.subjects?.generated ?? []).filter(
    (subject) =>
      subject.subjectSchemeIdentifier === SubjectSchemeIdentifier.Keyword,
  ),
  final: (product?.subjects?.final ?? []).filter(
    (subject) =>
      subject.subjectSchemeIdentifier === SubjectSchemeIdentifier.Keyword,
  ),
  finalWords: (product?.subjects?.finalWords ?? []).filter(
    (subject) =>
      subject.subjectSchemeIdentifier === SubjectSchemeIdentifier.Keyword,
  ),
});

export const getThemaCodesByType = (
  product?: IProduct,
): Record<ProductSubjectType, FirestoreProductSubject[]> => ({
  database: (product?.subjects?.database ?? []).filter((subject) =>
    [...qualifierTypes, subjectsType].includes(
      subject.subjectSchemeIdentifier as SubjectSchemeIdentifier,
    ),
  ),
  generated: (product?.subjects?.generated ?? []).filter((subject) =>
    [...qualifierTypes, subjectsType].includes(
      subject.subjectSchemeIdentifier as SubjectSchemeIdentifier,
    ),
  ),
  final: (product?.subjects?.final ?? []).filter((subject) =>
    [...qualifierTypes, subjectsType].includes(
      subject.subjectSchemeIdentifier as SubjectSchemeIdentifier,
    ),
  ),
});

const getKeywordsBySubjectType = (
  type: ProductSubjectType,
  product?: IProduct,
): string[] => {
  const keywords = getKeywordsByType(product)[type];
  return keywordsToText(keywords);
};

export const getFinalKeywords = (product?: IProduct) =>
  getKeywordsBySubjectType("final", product);
